import { splitLongLyricLines } from "./lyric-split.js";

export const fmt = (seconds) => {
  const n = Number.isFinite(seconds) && seconds >= 0 ? Math.floor(seconds) : 0;
  return `${String(Math.floor(n / 60)).padStart(2, "0")}:${String(n % 60).padStart(2, "0")}`;
};
export function localPath(value) {
  if (!value) return "";
  try {
    const u = new URL(value, "https://music.invalid");
    return u.pathname + u.search;
  } catch {
    return "";
  }
}
export function mediaUrl(value) {
  if (!value) return "";
  try {
    const u = new URL(
      value,
      globalThis.location?.href || "https://music.invalid",
    );
    if (!["https:", "http:"].includes(u.protocol)) return "";
    if (u.protocol === "http:") u.protocol = "https:";
    return u.href;
  } catch {
    return "";
  }
}
export function parseLyrics(raw) {
  let offset = 0;
  const found = raw.match(/\[offset:([+-]?\d+)\]/i);
  if (found) offset = Number(found[1]) / 1000;
  const lines = [];
  for (const line of raw.split(/\r?\n/)) {
    const tags = [
      ...line.matchAll(/[\[<](\d+):(\d{1,2})(?:[.:](\d{1,3}))?[\]>]/g),
    ].filter((tag) => Number(tag[2]) < 60);
    const text = line.replace(/\[[^\]]*\]|<\d+:\d+(?:[.:]\d+)?>/g, "").trim();
    const at = (tag) =>
      +tag[1] * 60 + +tag[2] + Number("0." + (tag[3] || "0")) + offset;
    const pieces = tags.map((tag, index) => ({
      time: at(tag),
      end: tags[index + 1] ? at(tags[index + 1]) : null,
      text: line.slice(
        tag.index + tag[0].length,
        tags[index + 1]?.index ?? line.length,
      ),
    }));
    if (pieces.slice(0, -1).some((piece) => piece.text.trim())) {
      const words = pieces.filter((piece) => piece.text.length);
      if (words.length)
        lines.push({
          time: words[0].time,
          text: words
            .map((w) => w.text)
            .join("")
            .trim(),
          words,
        });
      continue;
    }
    for (const tag of tags)
      lines.push({
        time: +tag[1] * 60 + +tag[2] + Number("0." + (tag[3] || "0")) + offset,
        text,
      });
  }
  if (lines.length === 0) {
    // LRC without timestamps: still show the text lines so lyrics are not blank.
    const lengthMatch = raw.match(/\[length:\s*(\d+):(\d{1,2})(?:[.:](\d{1,3}))?\]/i);
    const total = lengthMatch
      ? +lengthMatch[1] * 60 + +lengthMatch[2] + Number("0." + (lengthMatch[3] || "0"))
      : 0;
    const plain = raw
      .split(/\r?\n/)
      .map((l) => l.replace(/\[[^\]]*\]|<[^>]*>/g, "").trim())
      .filter((t) => t.length > 0);
    const step = total > 0 && plain.length > 1 ? total / (plain.length - 1) : 2;
    for (let i = 0; i < plain.length; i++) {
      lines.push({ time: Math.max(0, i * step - step * 0.5), text: plain[i] });
    }
  }
  return lines.sort((a, b) => a.time - b.time);
}
// YRC timing and translation matching follow Lazer's lyric_document.dart.
export function parseYrc(raw = "") {
  const lines = [];
  for (const line of raw.split(/\r?\n/)) {
    const match = line.match(/^\[(-?\d+)[,，](-?\d+)\](.*)$/);
    if (!match) continue;
    const words = [
      ...match[3].matchAll(
        /\((\d+)[,，](\d+)[,，](\d+)\)(.*?)(?=\(\d+[,，]\d+[,，]\d+\)|$)/g,
      ),
    ]
      .filter((m) => m[4])
      .map((m) => ({
        time: Number(m[1]) / 1000,
        end: (Number(m[1]) + Math.max(1, Number(m[2]))) / 1000,
        text: m[4],
      }));
    if (words.length)
      lines.push({
        time: Number(match[1]) / 1000,
        end: (Number(match[1]) + Number(match[2])) / 1000,
        text: words.map((w) => w.text).join(""),
        words,
      });
  }
  return lines.sort((a, b) => a.time - b.time);
}
export function attachTranslations(lines, translated) {
  const useful = translated.filter(
    (t) => t.text?.trim() || t.translation?.trim(),
  );
  return lines.map((line) => {
    let nearest;
    for (const candidate of useful) {
      const distance = Math.abs(candidate.time - line.time);
      if (
        distance <= 1.2 &&
        (!nearest || distance < Math.abs(nearest.time - line.time))
      )
        nearest = candidate;
    }
    return {
      ...line,
      translation: nearest
        ? nearest.translation || nearest.text
        : line.translation || "",
    };
  });
}
export function normalizeLocal(song) {
  return {
    key: "local:" + (song.dbId || song.fileName || song.src),
    id: song.dbId || song.fileName,
    source: "local",
    title: song.title || "未命名曲目",
    artist: song.singer || "未知歌手",
    album: song.album || "未归类曲目",
    cover: localPath(song.coverImgUrl),
    url: localPath(song.src),
    lyric: localPath(
      song.lrc ||
        song.lyricsUrl ||
        song.src?.replace(/\.[^/.?]+(?:\?.*)?$/, ".lrc"),
    ),
    duration: Number(song.duration) || 0,
  };
}
export function normalizeCloud(song) {
  return {
    key: "cloud:" + song.id,
    artists: (song.ar || song.artists || []).map(a => ({id:a.id,name:a.name})),
    id: song.id,
    source: "cloud",
    title: song.name || "未命名曲目",
    artist:
      (song.ar || song.artists || []).map((a) => a.name).join(" / ") ||
      "未知歌手",
    album: (song.al || song.album)?.name || "网易云音乐",
    cover: mediaUrl((song.al || song.album)?.picUrl),
    duration: (song.dt || song.duration || 0) / 1000,
  };
}
export async function json(url, signal) {
  const response = await fetch(url, {
    signal: signal
      ? AbortSignal.any([signal, AbortSignal.timeout(20000)])
      : AbortSignal.timeout(20000),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const error = new Error(body.error || body.message || `服务返回 ${response.status}`);
    error.status = response.status;
    throw error;
  }
  const data = await response.json();
  if (data.code && ![200, 201].includes(data.code))
    throw new Error(data.message || data.msg || "服务暂不可用");
  return data;
}
export function nextIndex(
  length,
  index,
  mode,
  ended = false,
  random = Math.random,
) {
  if (!length) return -1;
  if (mode === "one" && ended) return Math.max(0, index);
  if (mode === "shuffle" && length > 1)
    return (
      (Math.max(0, index) + 1 + Math.floor(random() * (length - 1))) % length
    );
  return (index + 1) % length;
}
export class Player extends EventTarget {
  constructor() {
    super();
    this.audio = new Audio();
    this.audio.preload = "metadata";
    this.queue = [];
    this.index = -1;
    this.token = 0;
    this.lyrics = [];
    this.lineLyrics = [];
    this.wordLyrics = [];
    this.lyricMode = "line";
    this.splitLongLyrics = true;
    this.lyricOffset = 0;
    this.translations = [];
    this.translationState = "idle";
    this.translationProvider = "";
    this.translationError = "";
    this.autoTranslationEnabled = true;
    this.quality = "exhigh";
    this.preferOriginalFlac = false;
    this.playbackFormat = "";
    this.requestedQuality = "";
    this.mode = "all";
    this.loading = false;
    this.failed = false;
    this.sourceState = null;
    this.buffering = false;
    this.audio.volume = 0.7;
    for (const name of [
      "play",
      "pause",
      "loadedmetadata",
      "timeupdate",
      "volumechange",
      "durationchange",
      "waiting",
      "playing",
    ])
      this.audio.addEventListener(name, () => {
        if (name === "playing" || name === "pause") this.loading = false;
        if (name === "waiting") this.buffering = true;
        if (["playing", "pause", "loadedmetadata"].includes(name))
          this.buffering = false;
        this.emit();
      });
    this.audio.addEventListener("ended", () => this.next(true));
    this.audio.addEventListener("error", () => {
      if (this.audio.getAttribute("src")) {
        if (this.playbackFormat === "FLAC 原件" && this.current?.url) {
          this.compatFallback = this.fallbackToCompat(this.token);
          void this.compatFallback.catch((error) => {
            this.failed = true;
            this.loading = false;
            this.sourceState = { message: error.message || "音源无法播放" };
            this.emit(error.message || "音源无法播放");
          });
          return;
        }
        this.failed = true;
        this.loading = false;
        this.sourceState = { message: "当前音频格式无法播放，请重试或选择其他曲目" };
        this.emit("音源无法播放，请重试或选择其他曲目");
      }
    });
  }
  adjustLyricOffset(delta) {
    this.lyricOffset = Math.max(-5, Math.min(5, Math.round((this.lyricOffset + delta) * 10) / 10));
    this.emit();
  }
  get current() {
    return this.queue[this.index];
  }
  emit(error) {
    this.dispatchEvent(new CustomEvent("change", { detail: { error } }));
  }
  async fallbackToCompat(token) {
    if (token !== this.token || this.playbackFormat !== "FLAC 原件") return;
    const song = this.current;
    this.playbackFormat = "MP3 320 kbps";
    const position = this.audio.currentTime || 0;
    this.audio.src = song.url;
    this.audio.load();
    if (position > 0)
      this.audio.addEventListener("loadedmetadata", () => {
        if (token === this.token && Number.isFinite(this.audio.duration))
          this.audio.currentTime = Math.min(position, this.audio.duration);
      }, { once: true });
    this.emit("当前浏览器无法解码这首 FLAC，已切换到 MP3 兼容版");
    await this.audio.play();
  }
  async play(song, list, queueIndex) {
    if (!song) return;
    if (list) this.queue = [...list];
    this.index = queueIndex ?? this.queue.findIndex((s) => s.key === song.key);
    if (this.index < 0) {
      this.queue.push(song);
      this.index = this.queue.length - 1;
    }
    const token = ++this.token;
    this.abort?.abort();
    this.abort = new AbortController();
    const signal = this.abort.signal;
    this.audio.pause();
    this.audio.removeAttribute("src");
    this.audio.load();
    this.lyrics = [];
    this.lineLyrics = [];
    this.wordLyrics = [];
    this.translations = [];
    this.translationState = "idle";
    this.translationProvider = "";
    this.translationError = "";
    this.failed = false;
    this.sourceState = null;
    this.loading = true;
    this.compatFallback = null;
    this.playbackFormat = "";
    this.requestedQuality = "";
    this.emit();
    try {
      let url = song.url;
      if (song.source === "cloud") {
        let entry;
        let sourceError;
        song.preview = false;
        try {
          const bound = await json(
            "/music/library/netease/song-url/" + encodeURIComponent(song.id) +
              "?level=" + encodeURIComponent(this.quality),
            signal,
          );
          entry = bound.data?.[0];
        } catch (error) {
          if (signal.aborted) throw error;
          if (error.status === 401) throw error;
          if (error.status !== 404) sourceError = error;
        }
        if (!entry?.url) try {
          const data = await json(
            "/netease/song/url/v1?id=" +
              encodeURIComponent(song.id) +
              "&level=" +
              this.quality,
            signal,
          );
          entry = data.data?.[0];
        } catch (error) {
          if (signal.aborted) throw error;
          sourceError ||= error;
        }
        if (!entry?.url) {
          try {
            const data = await json(
              "/netease/song/url?id=" + encodeURIComponent(song.id) + "&br=320000",
              signal,
            );
            entry = data.data?.[0];
          } catch (error) {
            if (signal.aborted) throw error;
            sourceError ||= error;
          }
        }
        url = mediaUrl(entry?.url);
        if (!url) {
          let alternative = null;
          try {
            const result = await json("/music/library/netease/alternative/" + encodeURIComponent(song.id), signal);
            if (result.alternative && String(result.alternative.id) !== String(song.id))
              alternative = normalizeCloud(result.alternative);
          } catch (error) {
            if (signal.aborted) throw error;
          }
          if (token !== this.token) return;
          const message = sourceError && !entry
            ? "音源服务暂不可用，请稍后重试"
            : entry?.freeTrialInfo
              ? "这首歌仅提供试听，完整音源不可用"
              : "这首歌暂无可用音源，可能受版权或账号权限限制";
          this.sourceState = { message, alternative };
          throw new Error(message);
        }
        song.preview = Boolean(entry.freeTrialInfo);
        this.requestedQuality = ({ standard: "标准", higher: "较高", exhigh: "极高", lossless: "无损", hires: "高解析度" })[this.quality] || this.quality;
        const kind = String(entry.type || entry.encodeType || new URL(url).pathname.match(/\.([a-z0-9]+)$/i)?.[1] || "").toUpperCase();
        const bitrate = Number(entry.br) > 0 ? ` ${Math.round(Number(entry.br) / 1000)} kbps` : "";
        const actual = kind ? kind + bitrate : bitrate.trim() || "在线音源";
        this.playbackFormat = `${actual}（请求${this.requestedQuality}）`;
      }
      if (token !== this.token) return;
      if (!url) throw new Error("没有可用的音乐地址");
      if (song.source === "local" && song.browserAudioFormat) {
        const original = this.preferOriginalFlac && song.originalUrl;
        url = original ? song.originalUrl : song.url;
        this.playbackFormat = original ? "FLAC 原件" : song.browserAudioFormat;
      }
      this.audio.src = url;
      this.loadLyrics(song, token, signal);
      await this.audio.play();
      if (token !== this.token) return;
      this.loading = false;
      this.emit(song.preview ? "当前音源为试听片段" : undefined);
      if ("mediaSession" in navigator) {
        navigator.mediaSession.metadata = new MediaMetadata({
          title: song.title,
          artist: song.artist,
          album: song.album,
          ...(song.cover
            ? { artwork: [{ src: new URL(song.cover, location.href).href }] }
            : {}),
        });
      }
    } catch (error) {
      if (token !== this.token || error.name === "AbortError") return;
      if (this.compatFallback) {
        try { await this.compatFallback; return; }
        catch (fallbackError) { error = fallbackError; }
      }
      if (this.playbackFormat === "FLAC 原件") {
        try {
          await this.fallbackToCompat(token);
          return;
        } catch (fallbackError) {
          error = fallbackError;
        }
      }
      this.loading = false;
      this.failed = error.name !== "NotAllowedError";
      if (this.failed)
        this.sourceState ||= { message: error.message || "音源播放失败" };
      this.emit(
        error.name === "NotAllowedError"
          ? "浏览器已暂停自动播放，请点击播放按钮"
          : error.message,
      );
    }
  }
  async playAlternative() {
    const alternative = this.sourceState?.alternative;
    if (!this.failed || !alternative || alternative.key === this.current?.key) return;
    this.queue.splice(this.index + 1, 0, alternative);
    await this.play(alternative, undefined, this.index + 1);
  }
  async loadLyrics(song, token, signal) {
    try {
      let raw = "";
      if (song.source === "local") {
        const [line, word] = await Promise.all([
          song.lyric
            ? fetch(song.lyric, { signal })
                .then((r) => (r.ok ? r.text() : ""))
                .catch(() => "")
            : "",
          song.wordLyric
            ? fetch(song.wordLyric, { signal })
                .then((r) => (r.ok ? r.text() : ""))
                .catch(() => "")
            : "",
        ]);
        if (token !== this.token) return;
        this.lineLyrics = parseLyrics(line);
        this.wordLyrics = parseLyrics(word);
        this.applyLyrics();
        this.emit();
        this.ensureTranslations();
        return;
      }
      if (song.source === "cloud") {
        let data;
        try {
          data = await json(
            "/netease/lyric/new?id=" + encodeURIComponent(song.id),
            signal,
          );
        } catch (error) {
          if (signal.aborted) throw error;
        }
        if (!data?.lrc?.lyric && !data?.yrc?.lyric)
          data = await json(
            "/netease/lyric?id=" + encodeURIComponent(song.id),
            signal,
          );
        raw = data.lrc?.lyric || "";
        if (token !== this.token) return;
        this.wordLyrics = parseYrc(data.yrc?.lyric || "");
        this.translations = parseLyrics(
          data.tlyric?.lyric || data.ytlrc?.lyric || "",
        );
        if (this.translations.length) {
          this.translationState = "ready";
          this.translationProvider = "音源译文";
        }
      }
      if (token === this.token) {
        this.lineLyrics = parseLyrics(raw);
        if (!this.wordLyrics.length)
          this.wordLyrics = this.lineLyrics.some((l) => l.words)
            ? this.lineLyrics
            : [];
        this.applyLyrics();
        this.emit();
        this.ensureTranslations();
      }
    } catch {
      if (token === this.token) this.emit();
    }
  }
  toggle(song, list) {
    if (!this.current || this.failed)
      return this.play(song || this.current, list);
    if (!this.audio.getAttribute("src")) return this.play(this.current);
    if (this.audio.paused) this.audio.play().catch((e) => this.emit(e.message));
    else this.audio.pause();
  }
  applyLyrics() {
    const lines =
      this.lyricMode === "word" && this.wordLyrics.length
        ? this.wordLyrics
        : this.lineLyrics.length
          ? this.lineLyrics
          : this.wordLyrics;
    const aligned = this.translations.length
      ? attachTranslations(lines, this.translations)
      : lines;
    this.lyrics = this.splitLongLyrics ? splitLongLyricLines(aligned) : aligned;
  }
  setSplitLongLyrics(enabled) {
    this.splitLongLyrics = Boolean(enabled);
    this.applyLyrics();
    this.emit();
  }
  setLyricMode(mode) {
    this.lyricMode = mode;
    this.applyLyrics();
    this.emit();
  }
  async ensureTranslations(force = false) {
    if (
      !this.autoTranslationEnabled ||
      !this.current ||
      !this.lyrics.length ||
      (!force &&
        ["pending", "ready", "original"].includes(this.translationState))
    )
      return;
    const song = this.current,
      token = this.token,
      signal = this.abort.signal;
    this.translationState = "pending";
    this.translationError = "";
    this.emit();
    const source = song.key.startsWith("library:")
      ? "library"
      : song.source === "cloud"
        ? "cloud"
        : "legacy";
    try {
      const response = await fetch("/music/library/translation", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Rhine-Request": "1" },
        body: JSON.stringify({ source, id: song.id }),
        signal,
      });
      let result = await response.json();
      if (!response.ok) throw new Error(result.error || "自动翻译暂不可用");
      for (let step = 0; step < 180; step++) {
        if (token !== this.token || signal.aborted) return;
        this.translationState = result.status;
        this.translationProvider = result.provider || "";
        this.translationProgress = result.total
          ? `${result.completed} / ${result.total}`
          : "";
        this.translations = (result.lines || [])
          .filter((l) => l.translation)
          .map((l) => ({ time: l.time, text: l.translation }));
        this.applyLyrics();
        this.emit();
        if (result.status !== "pending") {
          if (result.error) throw new Error(result.error);
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, 1200));
        if (signal.aborted) return;
        const r = await fetch("/music/library/translation/" + result.key, {
          signal,
        });
        result = await r.json();
        if (!r.ok) throw new Error(result.error || "自动翻译暂不可用");
      }
      throw new Error("自动翻译仍在处理，请稍后重试");
    } catch (error) {
      if (token !== this.token || signal.aborted) return;
      this.translationState = "error";
      this.translationError = error.message;
      this.emit();
    }
  }
  next(ended = false) {
    const index = nextIndex(this.queue.length, this.index, this.mode, ended);
    if (index >= 0) this.play(this.queue[index], undefined, index);
  }
  previous() {
    if (this.audio.currentTime > 3) {
      this.audio.currentTime = 0;
      return;
    }
    if (this.queue.length) {
      const index = (this.index - 1 + this.queue.length) % this.queue.length;
      this.play(this.queue[index], undefined, index);
    }
  }
  enqueue(song, next = false) {
    if (!song) return;
    this.queue.splice(next ? this.index + 1 : this.queue.length, 0, song);
    this.emit();
  }
  remove(index) {
    if (index === this.index) return;
    this.queue.splice(index, 1);
    if (index < this.index) this.index--;
    this.emit();
  }
  move(from, to) {
    if (!Number.isInteger(from) || !Number.isInteger(to) || from < 0 || to < 0 || from >= this.queue.length || to >= this.queue.length || from === to) return;
    const [song] = this.queue.splice(from, 1);
    this.queue.splice(to, 0, song);
    if (this.index === from) this.index = to;
    else if (from < this.index && to >= this.index) this.index--;
    else if (from > this.index && to <= this.index) this.index++;
    this.emit();
  }
  clearPending() {
    this.queue = this.current ? [this.current] : [];
    this.index = this.queue.length ? 0 : -1;
    this.emit();
  }
  seek(ratio) {
    if (Number.isFinite(this.audio.duration))
      this.audio.currentTime =
        Math.max(0, Math.min(1, ratio)) * this.audio.duration;
  }
}
